/**
 * Location Tracking Service
 * Handles periodic GPS updates to the backend for location sharing features
 */

import { profileApi } from '@api/endpoints/profile'
import { useSilentAdventureStore } from '@store/silentAdventureStore'
import type { Fix } from '@utils/preciseFix'

let locationInterval: ReturnType<typeof setInterval> | null = null
let isTracking = false

const UPDATE_INTERVAL_MS = 30000 // 30 seconds

/**
 * The least time allowed between two attempts to report, whatever asked for them.
 *
 * Every (re)start of tracking fires an immediate update, and the layout that starts it
 * stops it again in its effect cleanup — so anything that remounts the layout, or flips
 * `isAuthenticated`, re-fires that update with no regard for the 30s timer. Left
 * unguarded that has produced ~1,350 PUTs to /api/me/current-location in three seconds
 * from one tab. The 429 backoff in the API client cannot help: it only engages once a
 * 429 has come back, by which point hundreds are already in flight.
 *
 * Kept under UPDATE_INTERVAL_MS so the ordinary timer is never the one that gets skipped.
 */
const MIN_ATTEMPT_SPACING_MS = 20000

/**
 * A pending permission prompt means the browser never calls back, which would leave the
 * in-flight guard set for good. Past this long an attempt is assumed lost.
 */
const IN_FLIGHT_STALE_MS = 60000

let lastAttemptAt = 0
let attemptInFlight = false

/**
 * The newest attempt. Only its fix is reported; any older one that calls back is dropped.
 *
 * Chrome holds geolocation callbacks while a tab is hidden. Each attempt past IN_FLIGHT_STALE_MS
 * looked lost and was asked again, so a tab left in the background stacked one pending request a
 * minute, and on being shown again every one of them called back at once and sent its own PUT:
 * ~370 in a second from one account after seven hidden hours (2026-11-03).
 */
let latestAttempt = 0

const pageHidden = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden'

/** Back in view: report straight away rather than waiting up to 30s for the timer. */
function onVisibilityChange(): void {
  if (!pageHidden()) updateLocation()
}

/**
 * Start periodic location tracking
 * Updates user's current location every 30 seconds
 */
export async function startLocationTracking(): Promise<boolean> {
  if (!navigator.geolocation) {
    console.warn('[LocationTracking] Geolocation not supported by browser')
    return false
  }

  if (isTracking) {
    console.log('[LocationTracking] Already tracking')
    return true
  }

  // Note: We always send location updates regardless of sharing settings.
  // The backend needs location data to check for silent adventures.
  // Sharing settings only control what's visible to other users.

  console.log('[LocationTracking] Starting location tracking')
  isTracking = true

  // Initial update
  updateLocation()

  // Update every 30 seconds
  locationInterval = setInterval(updateLocation, UPDATE_INTERVAL_MS)
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibilityChange)

  return true
}

/**
 * Stop location tracking
 */
export function stopLocationTracking(): void {
  if (locationInterval) {
    clearInterval(locationInterval)
    locationInterval = null
  }
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibilityChange)
  isTracking = false
  console.log('[LocationTracking] Stopped location tracking')
}

/**
 * Check if location tracking is currently active
 */
export function isLocationTrackingActive(): boolean {
  return isTracking
}

/**
 * Perform a single location update
 */
function updateLocation(): void {
  const now = Date.now()

  // At most one report in flight, and at most one attempt per MIN_ATTEMPT_SPACING_MS. This is
  // the single funnel every report goes through, so the guard lives here rather than at each
  // of the callers that can start tracking.
  if (attemptInFlight && now - lastAttemptAt < IN_FLIGHT_STALE_MS) return
  if (now - lastAttemptAt < MIN_ATTEMPT_SPACING_MS) return
  // A hidden tab gets no fix until it is shown, so asking now only queues a request.
  if (pageHidden()) return

  attemptInFlight = true
  lastAttemptAt = now
  const attempt = ++latestAttempt

  navigator.geolocation.getCurrentPosition(
    async (position) => {
      if (attempt !== latestAttempt) return
      try {
        const response = await profileApi.updateCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed ?? undefined,
          timestamp: new Date().toISOString(),
        })
        console.log('[LocationTracking] Location updated:', {
          lat: position.coords.latitude.toFixed(4),
          lng: position.coords.longitude.toFixed(4),
          accuracy: Math.round(position.coords.accuracy),
        })
        acknowledgeSilentAdventures(response)
      } catch (error) {
        console.error('[LocationTracking] Failed to update location:', error)
      } finally {
        attemptInFlight = false
      }
    },
    (error) => {
      if (attempt !== latestAttempt) return
      attemptInFlight = false
      console.error('[LocationTracking] Geolocation error:', error.message)

      // Stop tracking if permission denied
      if (error.code === error.PERMISSION_DENIED) {
        console.warn('[LocationTracking] Permission denied, stopping tracking')
        stopLocationTracking()
      }
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
    }
  )
}

/**
 * Request a single immediate location update
 * Useful for on-demand updates outside the regular interval
 */
export function requestImmediateUpdate(): void {
  if (!navigator.geolocation) {
    console.warn('[LocationTracking] Geolocation not supported')
    return
  }
  updateLocation()
}

/**
 * Acknowledge any silent adventures auto-joined/completed at the reported location. Silent joins
 * are atomic join+complete, so both lists carry the discovery; the store dedupes by adventure id.
 * Handing off to the store lets MainLayout surface a toast from anywhere on the portal.
 */
function acknowledgeSilentAdventures(
  response: Awaited<ReturnType<typeof profileApi.updateCurrentLocation>> | undefined,
): void {
  const silent = response?.silentAdventures
  if (!silent) return
  const discovered = [...(silent.joinedAdventures ?? []), ...(silent.completedAdventures ?? [])]
  if (discovered.length > 0) {
    useSilentAdventureStore.getState().enqueue(discovered)
  }
}

/**
 * The least time between two on-demand reports. A join is a deliberate tap, so this only stops a
 * double-click (or a retry loop) from turning into a burst; the server's own floor coalesces
 * anything closer than 5 s from the same place anyway.
 */
const MIN_ON_DEMAND_SPACING_MS = 3000

let onDemandInFlight: Promise<void> | null = null
let lastOnDemandAt = 0

/**
 * Report a specific fix now, outside the 30 s cadence.
 *
 * Starting a run judges join criteria from the position the server has STORED, which the regular
 * report may have written up to 30 s (or, with the tab in the background, much longer) ago. A
 * join therefore reports the fix it was made from first. Concurrent calls share one request, and
 * calls closer than MIN_ON_DEMAND_SPACING_MS are dropped. It also counts as the periodic attempt,
 * so the timer does not report the same place again straight after. Never throws.
 */
export function reportLocationNow(fix: Fix): Promise<void> {
  if (onDemandInFlight) return onDemandInFlight
  const now = Date.now()
  if (now - lastOnDemandAt < MIN_ON_DEMAND_SPACING_MS) return Promise.resolve()
  lastOnDemandAt = now
  lastAttemptAt = now

  onDemandInFlight = profileApi
    .updateCurrentLocation({
      latitude: fix.latitude,
      longitude: fix.longitude,
      accuracy: fix.accuracy,
      timestamp: new Date(fix.timestamp).toISOString(),
    })
    .then((response) => acknowledgeSilentAdventures(response))
    .catch((error) => {
      console.error('[LocationTracking] On-demand location report failed:', error)
    })
    .finally(() => {
      onDemandInFlight = null
    })
  return onDemandInFlight
}
